/**
 * Utilities for rendering text on canvas with auto-scaling to fit dimensions.
 * Used by AdImageCreator for marketing image generation.
 */

/**
 * Measure text width for a given font string
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {string} font - e.g. "24px Arial"
 * @returns {number}
 */
export function measureTextWidth(ctx, text, font) {
  ctx.save()
  ctx.font = font
  const w = ctx.measureText(text).width
  ctx.restore()
  return w
}

/**
 * Split text into lines that fit within maxWidth (word wrap)
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth
 * @param {string} font
 * @returns {string[]}
 */
export function wrapText(ctx, text, maxWidth, font) {
  ctx.save()
  ctx.font = font
  const words = text.trim().split(/\s+/)
  const lines = []
  let current = ''
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    const width = ctx.measureText(candidate).width
    if (width <= maxWidth) {
      current = candidate
    } else {
      if (current) lines.push(current)
      current = word
    }
  }
  if (current) lines.push(current)
  ctx.restore()
  return lines
}

/**
 * Find a font size (within min–max) so that the text fits in the given box.
 * Uses word wrap and scales down (or up) until it fits, then returns
 * { fontSize, lines }.
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} maxWidth - inner width for text
 * @param {number} maxHeight - inner height for text
 * @param {string} fontFamily
 * @param {number} minFontSize
 * @param {number} maxFontSize
 * @returns {{ fontSize: number, lines: string[] }}
 */
export function fitTextInBox(ctx, text, maxWidth, maxHeight, fontFamily, minFontSize = 12, maxFontSize = 120) {
  if (!text || !text.trim()) {
    return { fontSize: maxFontSize, lines: [] }
  }
  let low = minFontSize
  let high = maxFontSize
  let best = { fontSize: minFontSize, lines: [] }
  const padding = 4
  const lineHeightMultiplier = 1.2
  const usableHeight = maxHeight - padding * 2
  const usableWidth = maxWidth - padding * 2

  while (low <= high) {
    const fontSize = Math.floor((low + high) / 2)
    const font = `bold ${fontSize}px ${fontFamily}`
    const lines = wrapText(ctx, text, usableWidth, font)
    const lineHeight = fontSize * lineHeightMultiplier
    const totalHeight = lines.length * lineHeight
    if (totalHeight <= usableHeight && lines.length > 0) {
      best = { fontSize, lines }
      low = fontSize + 1
    } else {
      high = fontSize - 1
    }
  }
  return best
}

/**
 * Draw text on canvas centered in the given rectangle, with optional shadow/outline for readability
 * @param {CanvasRenderingContext2D} ctx
 * @param {string[]} lines
 * @param {number} x - center x of text box
 * @param {number} y - center y of text box (vertical center of block)
 * @param {number} fontSize
 * @param {string} fontFamily
 * @param {string} fillColor - e.g. "#ffffff"
 * @param {object} options - { shadowColor, shadowBlur, strokeColor, strokeWidth }
 */
export function drawTextBlock(ctx, lines, x, y, fontSize, fontFamily, fillColor, options = {}) {
  const { shadowColor = 'rgba(0,0,0,0.5)', shadowBlur = 8, strokeColor = 'rgba(0,0,0,0.8)', strokeWidth = 2 } = options
  const lineHeight = fontSize * 1.2
  const totalHeight = lines.length * lineHeight
  let currentY = y - totalHeight / 2 + lineHeight / 2

  ctx.save()
  ctx.font = `bold ${fontSize}px ${fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  for (const line of lines) {
    if (shadowBlur) {
      ctx.shadowColor = shadowColor
      ctx.shadowBlur = shadowBlur
      ctx.shadowOffsetX = 1
      ctx.shadowOffsetY = 1
    }
    if (strokeWidth && strokeColor) {
      ctx.strokeStyle = strokeColor
      ctx.lineWidth = strokeWidth
      ctx.strokeText(line, x, currentY)
    }
    ctx.fillStyle = fillColor
    ctx.fillText(line, x, currentY)
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
    currentY += lineHeight
  }
  ctx.restore()
}

/**
 * Draw background image (stretched to fill canvas), then optional overlay for text area
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement} img
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 */
export function drawBackground(ctx, img, canvasWidth, canvasHeight) {
  if (!img || !img.complete) return
  ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight)
}

/**
 * Get the safe area for text (padding from edges). Returns { x, y, width, height } in canvas coords
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 * @param {number} paddingPercent - 0–50, default 10
 */
export function getTextSafeArea(canvasWidth, canvasHeight, paddingPercent = 10) {
  const padX = (canvasWidth * paddingPercent) / 100
  const padY = (canvasHeight * paddingPercent) / 100
  return {
    x: canvasWidth / 2,
    y: canvasHeight / 2,
    width: canvasWidth - padX * 2,
    height: canvasHeight - padY * 2
  }
}
